import React, { useState } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid,
  Link as MuiLink 
} from "@mui/material";
import { keyframes, textAlign } from "@mui/system";
import bg from '../../assets/images/bicycle_game/bg_hp.png'
import bicycle from '../../assets/images/bicycle_game/biciclist_hp.png'
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { generateUserId } from '../../features/games/bicycleGameSlice';
import theme from '../../styles/themes/default';
import prize1 from '../../assets/images/bicycle_game/premiu1_hp.png'
import prize2 from '../../assets/images/bicycle_game/premiul2_hp.png'
import lampPost from '../../assets/images/bicycle_game/stalp_hp.png'
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link } from 'react-router-dom';

interface KozelCampaignProps {
  onFormSuccess?: (userId: string) => void;
  onStartGame?: () => void;
}

  const KozelCampaign: React.FC<KozelCampaignProps> = ({ onFormSuccess, onStartGame }) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.bicycleGame);

    // Funcție comună pentru a obține sau genera un ID
    const getOrGenerateUserId = async (): Promise<string> => {
      // Verificăm mai întâi în localStorage
      const storedUserId = localStorage.getItem('bicycleGameUserId');
      if (storedUserId) {
        return storedUserId;
      }
      
      // Apoi verificăm în starea Redux
      if (user) {
        // Salvăm ID-ul din Redux și în localStorage pentru consistență
        localStorage.setItem('bicycleGameUserId', user);
        return user;
      }
      
      // Dacă nu există nicăieri, generăm unul nou
      try {
        setLoading(true);
        const newUserId = await dispatch(generateUserId()).unwrap();
        if (!newUserId) {
          throw new Error('Could not generate user ID');
        }
        
        // Salvăm noul ID în localStorage
        localStorage.setItem('bicycleGameUserId', newUserId);
        return newUserId;
      } catch (error) {
        console.error('Error generating user ID:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    };

    const openRegulation = (pdfName: string) => {
    // Deschide PDF-ul într-un tab nou pentru vizualizare
    window.open(`${pdfName}`, '_blank');
  };

  return (
    <Box 
      sx={{ 
        minHeight: '80vh',
        backgroundImage: `url(${bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: 4,
        fontFamily: theme.fonts.apex,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h1" 
            component="h1" 
            sx={{ 
              fontSize: { xs: '2.5rem', md: '4rem' }, 
              color: '#e9d4a8',
              textTransform: 'uppercase',
              mb: 2,
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.poplar
            }}
          >
            MEEEEEREU ÎMPREUNĂ
          </Typography>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              color: '#e9d4a8',
              textTransform: 'uppercase',
              fontFamily: theme.fonts.poplar
            }}
          >
            TU PEDALEZI. KOZEL DONEAZĂ.
          </Typography>
        </Box>

        {/* Main Content */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Left side - Cyclist */}
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ position: 'relative', marginBottom: 3 }}>
              <Box
                component="img"
                src={lampPost}
                alt="Lamp Post Left"
                sx={{ 
                  position: 'absolute',
                  left: '-75%',
                  bottom: '0',
                  height: '100%',
                  zIndex: 1,
                  display: { xs: 'none', md: 'block' }
                }}
              />
              
              {/* Lamp posts behind cyclist */}
              <Box
                component="img"
                src={lampPost}
                alt="Lamp Post Right Back"
                sx={{ 
                  position: 'absolute',
                  left: '-5%',
                  bottom: '0',
                  height: '100%',
                  zIndex: 1,
                  opacity: 0.8,
                  display: { xs: 'none', md: 'block' }
                }}
              />
              
              <Box
                component="img"
                src={lampPost}
                alt="Lamp Post Far Right"
                sx={{ 
                  position: 'absolute',
                  right: '-5%',
                  bottom: '0',
                  height: '100%',
                  zIndex: 1,
                  opacity: 0.7,
                  display: { xs: 'none', md: 'block' }
                }}
              />

              <Box
                component="img"
                src={lampPost}
                alt="Lamp Post Far Right"
                sx={{ 
                  position: 'absolute',
                  right: '-125%',
                  bottom: '0',
                  height: '100%',
                  zIndex: 1,
                  opacity: 0.3,
                  display: { xs: 'none', md: 'block' }
                }}
              />

              <Box
                component="img"
                src={lampPost}
                alt="Lamp Post Far Right"
                sx={{ 
                  position: 'absolute',
                  right: '-195%',
                  bottom: '0',
                  height: '100%',
                  zIndex: 1,
                  opacity: 0.3,
                  display: { xs: 'none', md: 'block' }
                }}
              />
              {/* Cyclist image */}
              <Box
                component="img"
                src={bicycle}
                alt="Cyclist"
                sx={{ 
                  maxWidth: '100%',
                  height: 'auto',
                  position: 'relative',
                  zIndex: 2
                }}
              />
            </Box>

            <Button
              component={Link}
              to="/mereu-impreuna-pedalezi-si-iluminezi"
              disabled={loading}
              sx={{
                backgroundColor: '#f5e8c9',
                color: '#4a3824',
                fontWeight: 'bold',
                padding: '10px 20px',
                width: '350px',
                fontSize: '1.2rem',
                '&:hover': {
                  backgroundColor: '#ffffff',
                },
                borderRadius: '4px',
                boxShadow: 'none',
                textTransform: 'uppercase',
                marginTop: 2
              }}
            >
              {loading ? "SE ÎNCARCĂ..." : "ACCESEAZĂ CAMPANIA"}
            </Button>
          </Grid>
          
          {/* Right side - Prizes */}
          <Grid item xs={12} md={6} sx={{mt: 10}}>
            <Typography 
              variant="h3" 
              component="h3" 
              sx={{ 
                mb: 4, 
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                color: '#e9d4a8',
                textTransform: 'uppercase',
                fontFamily: theme.fonts.apex
              }}
            >
              POȚI CÂȘTIGA:
            </Typography>
            
            {/* Prize 1 */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Box
              sx={{
                width: { xs: 100, sm: 100 },
                height: { xs: 100, sm: 100 },
                borderRadius: '50%',
                backgroundColor: 'white',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mr: { xs: 2, sm: 3 },
                overflow: 'hidden',
                boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.4)',
                flexShrink: 0,
                aspectRatio: '1/1',
              }}
            >
                <Box
                  component="img"
                  src={prize1}
                  alt="Bicicletă electrică"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Box>
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#e9d4a8',
                    mb: 1
                  }}
                >
                  Prin tragere la sorți:
                </Typography>
                <Typography 
                  variant="h6" 
                  component="p" 
                  sx={{ 
                    color: '#e9d4a8',
                    fontWeight: 'bold',
                    fontFamily: theme.fonts.apex
                  }}
                >
                  3 BICICLETE<br />ELECTRICE
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#e9d4a8',
                    fontFamily: theme.fonts.apex
                  }}
                >
                  Perioada Campaniei: 15 mai - 31 iulie 2025
                </Typography>
                <MuiLink
                  component="button"
                  onClick={() => openRegulation('/pdf/2025/Regulament autentificat_Kozel @ online.pdf')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#f5e8c9',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    mt: 1,
                    '&:hover': {
                      color: '#ffffff'
                    }
                  }}
                >
                  <VisibilityIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                  <Typography variant="body2">
                    Vezi regulamentul
                  </Typography>
                </MuiLink>
              </Box>
            </Box>
            
            {/* Prize 2 */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Box
              sx={{
                width: { xs: 100, sm: 100 },
                height: { xs: 100, sm: 100 },
                borderRadius: '50%',
                backgroundColor: 'white',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mr: { xs: 2, sm: 3 },
                overflow: 'hidden',
                boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.4)',
                flexShrink: 0,
                aspectRatio: '1/1',
              }}
            >
                <Box
                  component="img"
                  src={prize2}
                  alt="Cupon 20% discount"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Box>
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#e9d4a8',
                    mb: 1
                  }}
                >
                  Pe loc:
                </Typography>
                <Typography 
                  variant="h6" 
                  component="p" 
                  sx={{ 
                    color: '#e9d4a8',
                    fontWeight: 'bold',
                    fontFamily: theme.fonts.apex
                  }}
                >
                  VOUCHERE 20% DISCOUNT<br />KOZEL IN KAUFLAND
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#e9d4a8',
                    fontFamily: theme.fonts.apex
                  }}
                >
                  Perioada Campaniei: 15 mai - 25 iunie 2025
                </Typography>
                <MuiLink
                  component="button"
                  onClick={() => openRegulation('/pdf/2025/Regulament_Kozel_Kaufland_final.pdf')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#f5e8c9',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    mt: 1,
                    '&:hover': {
                      color: '#ffffff'
                    }
                  }}
                >
                  <VisibilityIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                  <Typography variant="body2">
                    Vezi regulamentul
                  </Typography>
                </MuiLink>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
      </Container>
    </Box>
  );
};

export default KozelCampaign;